import React, { useState, useEffect } from 'react';
import { FaEdit, FaSave, FaTimes, FaImage, FaPalette, FaTextHeight, FaCog } from 'react-icons/fa';
import TextEditor from './TextEditor';
import ImageUploader from './ImageUploader';
import ColorPicker from './ColorPicker';
import LayoutEditor from './LayoutEditor';

interface SectionPropsEditorProps {
  sectionType: string;
  currentProps: any;
  onSave: (newProps: any) => void;
  onCancel: () => void;
  language: 'ar' | 'en';
}

const SectionPropsEditor: React.FC<SectionPropsEditorProps> = ({
  sectionType,
  currentProps,
  onSave,
  onCancel,
  language
}) => {
  const [props, setProps] = useState<any>(currentProps);
  const [activeTab, setActiveTab] = useState<'content' | 'style' | 'layout'>('content');
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setProps(currentProps);
    setIsDirty(false);
  }, [currentProps]);

  const handlePropChange = (key: string, value: any) => {
    setProps((prev: any) => ({
      ...prev,
      [key]: value
    }));
    setIsDirty(true);
  };

  const handleSave = () => {
    onSave(props);
    setIsDirty(false);
  };

  const getSectionFields = () => {
    switch (sectionType) {
      case 'HeroSection':
        return {
          content: [
            { key: 'headline', type: 'text', label: { ar: 'العنوان الرئيسي', en: 'Headline' } },
            { key: 'subheadline', type: 'text', label: { ar: 'العنوان الفرعي', en: 'Subheadline' } },
            { key: 'images', type: 'images', label: { ar: 'الصور', en: 'Images' } }
          ],
          style: [
            { key: 'backgroundColor', type: 'color', label: { ar: 'لون الخلفية', en: 'Background Color' } },
            { key: 'textColor', type: 'color', label: { ar: 'لون النص', en: 'Text Color' } }
          ],
          layout: [
            { key: 'padding', type: 'spacing', label: { ar: 'المساحة الداخلية', en: 'Padding' } },
            { key: 'alignment', type: 'alignment', label: { ar: 'المحاذاة', en: 'Alignment' } }
          ]
        };

      case 'AboutHeroSection':
      case 'ProjectsHeroSection':
      case 'ContactHeroSection':
        return {
          content: [
            { key: 'titleAr', type: 'text', label: { ar: 'العنوان (عربي)', en: 'Title (Arabic)' } },
            { key: 'titleEn', type: 'text', label: { ar: 'العنوان (إنجليزي)', en: 'Title (English)' } },
            { key: 'subtitleAr', type: 'text', label: { ar: 'العنوان الفرعي (عربي)', en: 'Subtitle (Arabic)' } },
            { key: 'subtitleEn', type: 'text', label: { ar: 'العنوان الفرعي (إنجليزي)', en: 'Subtitle (English)' } }
          ],
          style: [
            { key: 'backgroundColor', type: 'color', label: { ar: 'لون الخلفية', en: 'Background Color' } },
            { key: 'textColor', type: 'color', label: { ar: 'لون النص', en: 'Text Color' } }
          ],
          layout: [
            { key: 'padding', type: 'spacing', label: { ar: 'المساحة الداخلية', en: 'Padding' } }
          ]
        };

      case 'AboutVisionMissionGoalsSection':
        return {
          content: [
            { key: 'visionAr', type: 'textarea', label: { ar: 'الرؤية (عربي)', en: 'Vision (Arabic)' } },
            { key: 'visionEn', type: 'textarea', label: { ar: 'الرؤية (إنجليزي)', en: 'Vision (English)' } },
            { key: 'missionAr', type: 'textarea', label: { ar: 'الرسالة (عربي)', en: 'Mission (Arabic)' } },
            { key: 'missionEn', type: 'textarea', label: { ar: 'الرسالة (إنجليزي)', en: 'Mission (English)' } },
            { key: 'goalsAr', type: 'textarea', label: { ar: 'الأهداف (عربي)', en: 'Goals (Arabic)' } },
            { key: 'goalsEn', type: 'textarea', label: { ar: 'الأهداف (إنجليزي)', en: 'Goals (English)' } }
          ],
          style: [
            { key: 'sectionBgColor', type: 'color', label: { ar: 'لون خلفية القسم', en: 'Section Background Color' } },
            { key: 'visionBgColor', type: 'color', label: { ar: 'لون خلفية كارت الرؤية', en: 'Vision Card Background Color' } },
            { key: 'missionBgColor', type: 'color', label: { ar: 'لون خلفية كارت الرسالة', en: 'Mission Card Background Color' } },
            { key: 'goalsBgColor', type: 'color', label: { ar: 'لون خلفية كارت الأهداف', en: 'Goals Card Background Color' } },
            { key: 'visionIconColor', type: 'color', label: { ar: 'لون أيقونة الرؤية', en: 'Vision Icon Color' } },
            { key: 'missionIconColor', type: 'color', label: { ar: 'لون أيقونة الرسالة', en: 'Mission Icon Color' } },
            { key: 'goalsIconColor', type: 'color', label: { ar: 'لون أيقونة الأهداف', en: 'Goals Icon Color' } }
          ],
          layout: [
            { key: 'padding', type: 'spacing', label: { ar: 'المساحة الداخلية', en: 'Padding' } }
          ]
        };

      case 'AboutWhyUsSection':
        return {
          content: [
            { key: 'titleAr', type: 'text', label: { ar: 'العنوان (عربي)', en: 'Title (Arabic)' } },
            { key: 'titleEn', type: 'text', label: { ar: 'العنوان (إنجليزي)', en: 'Title (English)' } },
            { key: 'showSection', type: 'select', label: { ar: 'إظهار القسم', en: 'Show Section' }, options: [true, false] },
            { key: 'layout', type: 'select', label: { ar: 'نوع التخطيط', en: 'Layout Type' }, options: ['grid', 'horizontal'] },
            { key: 'counters', type: 'counters', label: { ar: 'تخصيص العدادات', en: 'Customize Counters' } }
          ],
          style: [
            { key: 'backgroundColor', type: 'color', label: { ar: 'لون خلفية القسم', en: 'Section Background Color' } }
          ],
          layout: [
            { key: 'padding', type: 'spacing', label: { ar: 'المساحة الداخلية', en: 'Padding' } }
          ]
        };

      case 'AboutCompanyOverviewSection':
      case 'AboutQualitySafetySection':
      case 'AboutProjectManagementSection':
      case 'AboutCoreValuesSection':
      case 'AboutTestimonialsSection':
      case 'AboutCTASection':
        return {
          content: [
            { key: 'titleAr', type: 'text', label: { ar: 'العنوان (عربي)', en: 'Title (Arabic)' } },
            { key: 'titleEn', type: 'text', label: { ar: 'العنوان (إنجليزي)', en: 'Title (English)' } },
            { key: 'contentAr', type: 'textarea', label: { ar: 'المحتوى (عربي)', en: 'Content (Arabic)' } },
            { key: 'contentEn', type: 'textarea', label: { ar: 'المحتوى (إنجليزي)', en: 'Content (English)' } },
            { key: 'image', type: 'image', label: { ar: 'الصورة', en: 'Image' } }
          ],
          style: [
            { key: 'backgroundColor', type: 'color', label: { ar: 'لون الخلفية', en: 'Background Color' } },
            { key: 'textColor', type: 'color', label: { ar: 'لون النص', en: 'Text Color' } }
          ],
          layout: [
            { key: 'padding', type: 'spacing', label: { ar: 'المساحة الداخلية', en: 'Padding' } }
          ]
        };

      case 'ProjectsGridSection':
        return {
          content: [
            { key: 'projects', type: 'projects', label: { ar: 'المشاريع', en: 'Projects' } }
          ],
          style: [
            { key: 'backgroundColor', type: 'color', label: { ar: 'لون الخلفية', en: 'Background Color' } }
          ],
          layout: [
            { key: 'padding', type: 'spacing', label: { ar: 'المساحة الداخلية', en: 'Padding' } },
            { key: 'columns', type: 'select', label: { ar: 'عدد الأعمدة', en: 'Number of Columns' }, options: [1, 2, 3, 4] }
          ]
        };

      case 'ContactInfoSection':
        return {
          content: [
            { key: 'titleAr', type: 'text', label: { ar: 'العنوان (عربي)', en: 'Title (Arabic)' } },
            { key: 'titleEn', type: 'text', label: { ar: 'العنوان (إنجليزي)', en: 'Title (English)' } },
            { key: 'descriptionAr', type: 'textarea', label: { ar: 'الوصف (عربي)', en: 'Description (Arabic)' } },
            { key: 'descriptionEn', type: 'textarea', label: { ar: 'الوصف (إنجليزي)', en: 'Description (English)' } }
          ],
          style: [
            { key: 'backgroundColor', type: 'color', label: { ar: 'لون الخلفية', en: 'Background Color' } }
          ],
          layout: [
            { key: 'padding', type: 'spacing', label: { ar: 'المساحة الداخلية', en: 'Padding' } }
          ]
        };

      case 'VisionMissionSection':
      case 'WhyChooseUs':
      case 'ServicesSection':
      case 'BusinessScopeSection':
      case 'ImpactSection':
      case 'NewsSection':
      case 'TestimonialsSection':
      case 'ContactCallout':
      case 'MapSection':
      case 'Footer':
        return {
          content: [
            { key: 'titleAr', type: 'text', label: { ar: 'العنوان (عربي)', en: 'Title (Arabic)' } },
            { key: 'titleEn', type: 'text', label: { ar: 'العنوان (إنجليزي)', en: 'Title (English)' } },
            { key: 'subtitleAr', type: 'text', label: { ar: 'العنوان الفرعي (عربي)', en: 'Subtitle (Arabic)' } },
            { key: 'subtitleEn', type: 'text', label: { ar: 'العنوان الفرعي (إنجليزي)', en: 'Subtitle (English)' } }
          ],
          style: [
            { key: 'backgroundColor', type: 'color', label: { ar: 'لون الخلفية', en: 'Background Color' } },
            { key: 'textColor', type: 'color', label: { ar: 'لون النص', en: 'Text Color' } }
          ],
          layout: [
            { key: 'padding', type: 'spacing', label: { ar: 'المساحة الداخلية', en: 'Padding' } }
          ]
        };

      default:
        return {
          content: [
            { key: 'titleAr', type: 'text', label: { ar: 'العنوان (عربي)', en: 'Title (Arabic)' } },
            { key: 'titleEn', type: 'text', label: { ar: 'العنوان (إنجليزي)', en: 'Title (English)' } },
            { key: 'contentAr', type: 'textarea', label: { ar: 'المحتوى (عربي)', en: 'Content (Arabic)' } },
            { key: 'contentEn', type: 'textarea', label: { ar: 'المحتوى (إنجليزي)', en: 'Content (English)' } }
          ],
          style: [
            { key: 'backgroundColor', type: 'color', label: { ar: 'لون الخلفية', en: 'Background Color' } },
            { key: 'textColor', type: 'color', label: { ar: 'لون النص', en: 'Text Color' } }
          ],
          layout: [
            { key: 'padding', type: 'spacing', label: { ar: 'المساحة الداخلية', en: 'Padding' } }
          ]
        };
    }
  };

  const renderField = (field: any) => {
    const { key, type, label, options } = field;
    const value = props[key];
    const isRTL = language === 'ar';

    switch (type) {
      case 'text':
        return (
          <TextEditor
            key={key}
            label={isRTL ? label.ar : label.en}
            value={value || ''}
            onChange={(newValue: any) => handlePropChange(key, newValue)}
            language={language}
          />
        );

      case 'textarea':
        return (
          <div key={key} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isRTL ? label.ar : label.en}
            </label>
            <textarea
              value={value || ''}
              onChange={(e) => handlePropChange(key, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={4}
            />
          </div>
        );

      case 'image':
        return (
          <ImageUploader
            key={key}
            label={isRTL ? label.ar : label.en}
            value={value}
            onChange={(newValue: any) => handlePropChange(key, newValue)}
            language={language}
          />
        );

      case 'images':
        return (
          <ImageUploader
            key={key}
            label={isRTL ? label.ar : label.en}
            value={value}
            onChange={(newValue: any) => handlePropChange(key, newValue)}
            multiple={true}
            language={language}
          />
        );

      case 'color':
        return (
          <ColorPicker
            key={key}
            label={isRTL ? label.ar : label.en}
            value={value}
            onChange={(newValue: any) => handlePropChange(key, newValue)}
            language={language}
          />
        );

      case 'select':
        return (
          <div key={key} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isRTL ? label.ar : label.en}
            </label>
            <select
              value={value || options[0]}
              onChange={(e) => handlePropChange(key, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {options.map((option: any) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        );

      case 'counters':
        return (
          <div key={key} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              {isRTL ? label.ar : label.en}
            </label>
            <div className="space-y-4">
              {(value || []).map((counter: any, index: number) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">
                      {isRTL ? `عداد ${index + 1}` : `Counter ${index + 1}`}
                    </h4>
                    <button
                      onClick={() => {
                        const newCounters = [...(value || [])];
                        newCounters.splice(index, 1);
                        handlePropChange(key, newCounters);
                      }}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      {isRTL ? 'حذف' : 'Delete'}
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        {isRTL ? 'النص (عربي)' : 'Text (Arabic)'}
                      </label>
                      <input
                        type="text"
                        value={counter.ar || ''}
                        onChange={(e) => {
                          const newCounters = [...(value || [])];
                          newCounters[index] = { ...counter, ar: e.target.value };
                          handlePropChange(key, newCounters);
                        }}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        {isRTL ? 'النص (إنجليزي)' : 'Text (English)'}
                      </label>
                      <input
                        type="text"
                        value={counter.en || ''}
                        onChange={(e) => {
                          const newCounters = [...(value || [])];
                          newCounters[index] = { ...counter, en: e.target.value };
                          handlePropChange(key, newCounters);
                        }}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        {isRTL ? 'الرقم البداية' : 'Start Number'}
                      </label>
                      <input
                        type="number"
                        value={counter.startNumber || 0}
                        onChange={(e) => {
                          const newCounters = [...(value || [])];
                          newCounters[index] = { ...counter, startNumber: parseInt(e.target.value) || 0 };
                          handlePropChange(key, newCounters);
                        }}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        min="0"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        {isRTL ? 'الرقم النهائي' : 'End Number'}
                      </label>
                      <input
                        type="number"
                        value={counter.endNumber || 0}
                        onChange={(e) => {
                          const newCounters = [...(value || [])];
                          newCounters[index] = { ...counter, endNumber: parseInt(e.target.value) || 0 };
                          handlePropChange(key, newCounters);
                        }}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        min="0"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        {isRTL ? 'المدة (ثانية)' : 'Duration (seconds)'}
                      </label>
                      <input
                        type="number"
                        value={(counter.duration || 2000) / 1000}
                        onChange={(e) => {
                          const newCounters = [...(value || [])];
                          newCounters[index] = { ...counter, duration: (parseFloat(e.target.value) || 2) * 1000 };
                          handlePropChange(key, newCounters);
                        }}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        min="0.1"
                        step="0.1"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        {isRTL ? 'لون الخلفية' : 'Background Color'}
                      </label>
                      <input
                        type="color"
                        value={counter.color || '#3b82f6'}
                        onChange={(e) => {
                          const newCounters = [...(value || [])];
                          newCounters[index] = { ...counter, color: e.target.value };
                          handlePropChange(key, newCounters);
                        }}
                        className="w-full h-8 border border-gray-300 rounded cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <button
                onClick={() => {
                  const newCounters = [...(value || []), {
                    icon: 'FaAward',
                    number: '0+',
                    startNumber: 0,
                    endNumber: 10,
                    duration: 2000,
                    ar: 'عداد جديد',
                    en: 'New Counter',
                    color: '#3b82f6',
                    iconColor: '#ffffff'
                  }];
                  handlePropChange(key, newCounters);
                }}
                className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-colors"
              >
                {isRTL ? '+ إضافة عداد جديد' : '+ Add New Counter'}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const fields = getSectionFields();
  const isRTL = language === 'ar';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {isRTL ? 'تحرير خصائص السكشن' : 'Edit Section Properties'}
          </h3>
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <button
              onClick={handleSave}
              disabled={!isDirty}
              className={`px-4 py-2 rounded-md text-white font-medium ${
                isDirty ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              <FaSave className="inline mr-2" />
              {isRTL ? 'حفظ' : 'Save'}
            </button>
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded-md bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium"
            >
              <FaTimes className="inline mr-2" />
              {isRTL ? 'إلغاء' : 'Cancel'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('content')}
            className={`flex-1 px-4 py-3 text-sm font-medium ${
              activeTab === 'content'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FaEdit className="inline mr-2" />
            {isRTL ? 'المحتوى' : 'Content'}
          </button>
          <button
            onClick={() => setActiveTab('style')}
            className={`flex-1 px-4 py-3 text-sm font-medium ${
              activeTab === 'style'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FaPalette className="inline mr-2" />
            {isRTL ? 'التصميم' : 'Style'}
          </button>
          <button
            onClick={() => setActiveTab('layout')}
            className={`flex-1 px-4 py-3 text-sm font-medium ${
              activeTab === 'layout'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FaCog className="inline mr-2" />
            {isRTL ? 'التخطيط' : 'Layout'}
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {fields[activeTab]?.map(renderField)}
          
          {fields[activeTab]?.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              {isRTL ? 'لا توجد خصائص متاحة لهذا السكشن' : 'No properties available for this section'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionPropsEditor; 